import {type TranslationObject} from '@colonist/i18n'
import {createDivIn, createSpanIn} from '../ui_utils/DomUtils.ts'
import {ViewPopup} from './ViewPopup.ts'

export class ViewPopupPatchNoteImage extends ViewPopup {
    private loadingContainer: HTMLDivElement | null = null

    constructor(title: TranslationObject, imagePath: string) {
        super(title, undefined, () => undefined, () => undefined, true, {showXButton: false})

        this.container.classList.add('popup-patch-note-image')

        this.createLoadingSpinner()

        this.show()

        void this.loadImage(imagePath)
    }

    private createLoadingSpinner() {
        this.loadingContainer = createDivIn(this.body, 'popup-patch-note-image-loading', '')
        this.loadingContainer.setAttribute('role', 'status')
        this.loadingContainer.setAttribute('aria-live', 'polite')
        this.loadingContainer.setAttribute('aria-label', 'Loading image')

        const spinner = createDivIn(this.loadingContainer, 'loading-spinner', '')
        spinner.setAttribute('role', 'img')
        spinner.setAttribute('aria-label', 'Loading spinner')
    }

    private async loadImage(imagePath: string) {
        try {
            let imageUrl: string
            if(imagePath.startsWith('http')) {
                imageUrl = imagePath
            } else {
                const imageModule = await import(`@static/images/patch_notes${imagePath}`)
                imageUrl = imageModule.default
            }

            const altText = ViewPopupPatchNoteImage.getAltText(imagePath)

            const img = document.createElement('IMG') as HTMLImageElement
            img.alt = altText

            let imageLoaded = false
            let resolved = false

            await new Promise<void>((resolve) => {
                const handleResolve = () => {
                    if(resolved) return
                    resolved = true
                    resolve()
                }

                img.onload = () => {
                    imageLoaded = true
                    handleResolve()
                }
                img.onerror = () => {
                    handleResolve()
                }

                img.src = imageUrl

                setTimeout(() => {
                    if(!resolved && img.complete && img.naturalWidth > 0) {
                        imageLoaded = true
                        handleResolve()
                    }
                }, 0)
            })

            if(this.loadingContainer) {
                this.body.removeChild(this.loadingContainer)
                this.loadingContainer = null
            }

            if(!imageLoaded) {
                const errorText = createSpanIn(this.body, 'popup-patch-note-image-error', {key: 'strings:popups.patchNote.imageFailedToLoad'})
                errorText.setAttribute('role', 'alert')
                errorText.setAttribute('aria-live', 'assertive')
                return
            }

            this.body.appendChild(img)
        } catch(error) {
            if(this.loadingContainer) {
                this.body.removeChild(this.loadingContainer)
                this.loadingContainer = null
            }
            const errorText = createSpanIn(this.body, 'popup-patch-note-image-error', {key: 'strings:popups.patchNote.imageFailedToLoad'})
            errorText.setAttribute('role', 'alert')
            errorText.setAttribute('aria-live', 'assertive')
        }
    }

    private static getAltText(imagePath: string): string {
        return imagePath
            .replace(/\.[^.]+$/, '')
            .replace(/[_-]/g, ' ')
            .replace(/\b\w/g, (char) => char.toUpperCase())
    }
}

